import { PropertyConfig, PropertyRegistry, AnnotationType, PropertyType, PropertyValueType, PropertyValueData, ProjectUserPropertyConfig, ProjectGlobalPropertyConfig, CustomPropertyInputProps } from './types';

/**
 * Registry for managing parameter configurations
 * Allows registering, unregistering, and querying parameter definitions
 */
class PropertyRegistryImpl implements PropertyRegistry {
  private configs: Map<string, PropertyConfig> = new Map();

  /**
   * Register a new parameter configuration
   */
  register(config: PropertyConfig): void {
    if (this.configs.has(config.identifier)) {
      console.warn(`Parameter with identifier "${config.identifier}" is already registered. Overwriting.`);
    }

    // Validate config
    if (!this.validateConfig(config)) {
      console.error(`Failed to register property with identifier "${config.identifier}" due to validation errors`);
      return;
    }

    this.configs.set(config.identifier, { ...config });
  }

  /**
   * Unregister a parameter configuration
   */
  unregister(identifier: string): void {
    if (!this.configs.has(identifier)) {
      console.warn(`Parameter with identifier "${identifier}" is not registered.`);
      return;
    }

    this.configs.delete(identifier);
  }

  /**
   * Check if a parameter is registered
   */
  isRegistered(identifier: string): boolean {
    return this.configs.has(identifier);
  }

  /**
   * Get a specific parameter configuration
   */
  getConfig(identifier: string): PropertyConfig | undefined {
    return this.configs.get(identifier);
  }

  /**
   * Get all registered parameter configurations
   */
  getAllConfigs(): PropertyConfig[] {
    return Array.from(this.configs.values());
  }

  /**
   * Attach (or replace) a custom UI component on an already-registered property.
   * Pass `undefined` to clear it. Logs a warning if the property is not registered.
   */
  setCustomComponent<T extends PropertyValueType = PropertyValueType>(
    identifier: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: ((props: CustomPropertyInputProps<T>) => any) | undefined,
  ): void {
    const config = this.configs.get(identifier);
    if (!config) {
      console.warn(`Cannot set customComponent: property "${identifier}" is not registered.`);
      return;
    }
    // Stored as the wide form for the panel-side call site; the public API enforces
    // value-type correctness at registration.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config.ui = { ...(config.ui ?? {}), customComponent: component as any };
  }

  /**
   * Get parameter configurations for multiple annotation types
   * Only returns properties that explicitly include at least one of the requested annotation types
   */
  getConfigsForAnnotationTypes(annotationTypes: AnnotationType[]): PropertyConfig[] {
    return Array.from(this.configs.values()).filter((config) => {
      // Only project-level configs have annotationTypes
      if (config.type === PropertyType.PROJECT_USER || config.type === PropertyType.PROJECT_GLOBAL) {
        const projectConfig = config as ProjectUserPropertyConfig | ProjectGlobalPropertyConfig;
        // Properties with empty annotationTypes are excluded — they have dedicated UI
        // and should not appear in PropertyPanel auto-rendering.
        // Properties that should appear for all types must list them explicitly.
        return projectConfig.annotationTypes.length > 0 && annotationTypes.some((type) => projectConfig.annotationTypes.includes(type));
      }
      return false;
    });
  }

  /**
   * Get parameter configurations grouped by annotation type
   */
  getConfigsByAnnotationType(): Record<AnnotationType | 'ALL', PropertyConfig[]> {
    const result: Record<string, PropertyConfig[]> = {
      ALL: [],
      POINT: [],
      DISTANCE: [],
      AREA: [],
      BOX: [],
      POINTCLUSTER: [],
      PLANE: [],
    };

    this.configs.forEach((config) => {
      // Only project-level configs have annotationTypes
      if (config.type === PropertyType.PROJECT_USER || config.type === PropertyType.PROJECT_GLOBAL) {
        const projectConfig = config as ProjectUserPropertyConfig | ProjectGlobalPropertyConfig;
        if (projectConfig.annotationTypes.length === 0) {
          result.ALL.push(config);
        } else {
          projectConfig.annotationTypes.forEach((type: AnnotationType) => {
            result[type].push(config);
          });
        }
      }
    });

    return result as Record<AnnotationType | 'ALL', PropertyConfig[]>;
  }

  /**
   * Validate parameter configuration
   * @returns true if valid, false otherwise
   */
  private validateConfig(config: PropertyConfig): boolean {
    if (!config.identifier || typeof config.identifier !== 'string') {
      console.error('Parameter identifier must be a non-empty string');
      return false;
    }

    if (!config.name || typeof config.name !== 'string') {
      console.error('Parameter name must be a non-empty string');
      return false;
    }

    if (!Object.values(PropertyValueType).includes(config.valueType)) {
      console.error('Invalid parameter value type');
      return false;
    }

    if (!Object.values(PropertyType).includes(config.type)) {
      console.error('Invalid property type');
      return false;
    }

    // Only validate annotationTypes for project-level configs
    if (config.type === PropertyType.PROJECT_USER || config.type === PropertyType.PROJECT_GLOBAL) {
      const projectConfig = config as ProjectUserPropertyConfig | ProjectGlobalPropertyConfig;
      if (!Array.isArray(projectConfig.annotationTypes)) {
        console.error('Annotation types must be an array for project-level properties');
        return false;
      }

      // Validate annotation types
      for (const type of projectConfig.annotationTypes) {
        if (!Object.values(AnnotationType).includes(type)) {
          console.error(`Invalid annotation type: ${type}`);
          return false;
        }
      }
    }

    // Validate numeric constraints
    if ([PropertyValueType.FLOAT, PropertyValueType.INT, PropertyValueType.RANGE, PropertyValueType.COORDINATE].includes(config.valueType)) {
      if (config.validation?.min !== undefined && config.validation?.max !== undefined) {
        if (config.validation.min >= config.validation.max) {
          console.error('Minimum value must be less than maximum value');
          return false;
        }
      }
    }

    // Validate select options
    if (config.valueType === PropertyValueType.SELECT && (!config.validation?.options || config.validation.options.length === 0)) {
      console.error('Select parameters must have at least one option');
      return false;
    }

    return true;
  }

  /**
   * Validate a property value against its configuration
   * @param identifier - The property identifier
   * @param value - The value to validate
   * @returns An error message if validation fails, undefined if valid
   */
  validateValue(identifier: string, value: PropertyValueData): string | undefined {
    const config = this.configs.get(identifier);
    if (!config) {
      return `Property with identifier "${identifier}" is not registered`;
    }

    // Skip validation for read-only properties (their values are computed, not user-provided)
    if (config.readOnly) {
      return undefined;
    }

    // Type validation based on valueType
    switch (config.valueType) {
      case PropertyValueType.FLOAT:
      case PropertyValueType.INT:
      case PropertyValueType.RANGE:
        if (typeof value !== 'number') {
          return `Expected number for ${config.valueType}, got ${typeof value}`;
        }
        if (config.valueType === PropertyValueType.INT && !Number.isInteger(value)) {
          return `Expected integer value, got ${value}`;
        }
        break;
      case PropertyValueType.STRING:
        if (typeof value !== 'string') {
          return `Expected string, got ${typeof value}`;
        }
        break;
      case PropertyValueType.BOOLEAN:
        if (typeof value !== 'boolean') {
          return `Expected boolean, got ${typeof value}`;
        }
        break;
      case PropertyValueType.SELECT:
      case PropertyValueType.COLOR:
      case PropertyValueType.GRADIENT:
        if (typeof value !== 'string') {
          return `Expected string for ${config.valueType}, got ${typeof value}`;
        }
        break;
      case PropertyValueType.MIN_MAX:
        if (!Array.isArray(value) || value.length !== 2 || typeof value[0] !== 'number' || typeof value[1] !== 'number') {
          return `Expected [number, number] for MIN_MAX, got ${JSON.stringify(value)}`;
        }
        if (value[0] >= value[1]) {
          return `Minimum value must be less than maximum value`;
        }
        break;
      case PropertyValueType.COORDINATE: {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
          return `Expected object for COORDINATE, got ${typeof value}`;
        }
        const coord = value as { x?: unknown; y?: unknown; z?: unknown };
        if (typeof coord.x !== 'number' || typeof coord.y !== 'number' || typeof coord.z !== 'number') {
          return `Expected {x: number, y: number, z: number} for COORDINATE, got ${JSON.stringify(value)}`;
        }
        break;
      }
    }

    // Apply validation rules if defined
    if (config.validation) {
      const { required, min, max, pattern, options, custom } = config.validation;

      // Required validation
      if (required && (value === undefined || value === null || value === '')) {
        return `${config.name} is required`;
      }

      // Numeric validations
      if (typeof value === 'number') {
        if (min !== undefined && value < min) {
          return `Value must be at least ${min}`;
        }
        if (max !== undefined && value > max) {
          return `Value must be at most ${max}`;
        }
      }

      // String pattern validation
      if (typeof value === 'string' && pattern) {
        if (!pattern.test(value)) {
          return `Value does not match required pattern`;
        }
      }

      // Select options validation
      if (config.valueType === PropertyValueType.SELECT && options) {
        const validValues = options.filter((opt) => !opt.disabled).map((opt) => opt.value);
        if (!validValues.includes(value)) {
          return `Invalid option selected`;
        }
      }

      // Custom validation
      if (custom) {
        const customError = custom(value);
        if (customError) {
          return customError;
        }
      }
    }

    return undefined; // Valid
  }

  /**
   * Clear all registered configurations (useful for testing)
   */
  clear(): void {
    this.configs.clear();
  }

  /**
   * Get parameter count
   */
  getCount(): number {
    return this.configs.size;
  }
}

// Export singleton instance
export const propertyRegistry = new PropertyRegistryImpl();

// Export class for testing
export { PropertyRegistryImpl };
